import { useCallback, useEffect, useState, useSyncExternalStore, type ReactNode } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { api } from '@/lib/api/client';
import type { DescriptionRecord } from './store';
import { cn } from '@/lib/utils';
import { descriptionSymbol, getDescriptionSnapshot, subscribeDescription } from './store';
export { refreshDescription } from './store';
export type { DescriptionRecord } from './store';

/** Company-wide summary. Sector roles stay separate; missing evidence is visible. */
export function StockDescription({ symbol: raw, inline = false, className, expandable = false }: {
  symbol?: string | null; inline?: boolean; className?: string; expandable?: boolean;
}) {
  const symbol = descriptionSymbol(raw);
  const subscribe = useCallback((fn: () => void) => subscribeDescription(symbol, fn), [symbol]);
  const snapshot = useSyncExternalStore(subscribe, () => getDescriptionSnapshot(symbol), () => getDescriptionSnapshot(symbol));
  if (!symbol) return null;
  const record = snapshot.record;
  const label = snapshot.state === 'loading' ? '简介读取中…'
    : snapshot.state === 'error' ? '简介读取失败'
    : record?.description || '简介待补充';
  const source = record?.source;
  const title = [label, source?.label, source?.note, source?.url, source?.provider && `模型：${source.provider} / ${source.model || "未记录"}`,
    source?.revenue_period && `营收报告期：${source.revenue_period}`, source?.observed_at && `来源时间：${source.observed_at}`,
    record?.updated_at && `维护时间：${record.updated_at}`,
    source?.review_required && '自动资料尚待人工核验；可在系统 / 个股简介数据库修订',
  ].filter(Boolean).join('\n');
  const summary = <span data-stock-description={symbol} title={title}
    className={cn('stock-description font-sans text-[10px] font-normal leading-snug tracking-normal text-muted-foreground whitespace-normal break-words', inline ? 'ml-1 inline' : 'mt-0.5 block line-clamp-2', className)}>
    {inline && '（'}{label}{source?.review_required && record?.description && <span className="ml-1 opacity-70">· 待核验</span>}{inline && '）'}
  </span>;
  if (!expandable) return summary;
  return <Popover>
    <span className="flex min-w-0 items-center gap-1"><span className="min-w-0 flex-1">{summary}</span>
    <PopoverTrigger asChild><span role="button" tabIndex={0} aria-label="查看完整个股介绍"
      className="shrink-0 cursor-pointer rounded text-[9px] font-normal text-primary underline decoration-dotted underline-offset-2 hover:bg-accent focus-visible:outline focus-visible:outline-primary"
      onClick={e => e.stopPropagation()}
      onKeyDown={e => { e.stopPropagation(); if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.currentTarget.click(); } }}>全文</span></PopoverTrigger></span>
    <PopoverContent collisionPadding={12} className="w-[420px] max-w-[calc(100vw-24px)] p-4"
      onClick={e => e.stopPropagation()} onKeyDown={e => e.stopPropagation()}>
      <FullDescription key={symbol} symbol={symbol} />
    </PopoverContent>
  </Popover>;
}

/** Mounted only when the explicit full-text control opens. Summary batches stay small. */
function FullDescription({ symbol }: { symbol: string }) {
  const [detail, setDetail] = useState<(DescriptionRecord & { full_text: string | null; full_text_status: string }) | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setFailed(false);
    api.get<DescriptionRecord & { full_text: string | null; full_text_status: string }>(
      `/stock-descriptions/${encodeURIComponent(symbol)}`, { baseURL: '/api/v2', timeout: 15_000 },
    ).then(({ data }) => { if (active) setDetail(data); })
      .catch(() => { if (active) setFailed(true); });
    return () => { active = false; };
  }, [symbol, attempt]);
  const source = detail?.source;
  return <>
    <p className="mb-2 text-sm font-semibold">个股介绍 · {symbol}</p>
    {failed ? <p role="alert" className="text-sm">全文读取失败。<button className="ml-2 text-primary underline" onClick={() => setAttempt(n => n + 1)}>重试</button></p>
      : !detail ? <p role="status" className="text-sm">全文读取中…</p>
      : <div className="max-h-[min(60vh,480px)] overflow-y-auto whitespace-pre-wrap break-words text-sm leading-relaxed">{detail.full_text || '暂无完整介绍资料'}</div>}
    {detail?.full_text_status === 'partial' && <p className="mt-2 text-xs text-amber-600">已存来源仅有摘录，完整原文暂缺。</p>}
    {source?.review_required && <p className="mt-2 text-xs text-amber-600">待人工核验</p>}
    {source?.label && <p className="mt-2 text-xs text-muted-foreground">来源：{source.label}</p>}
    {typeof source?.observed_at === 'string' && <p className="mt-1 text-xs text-muted-foreground">资料时间：{String(source.observed_at)}</p>}
    {source?.note && <p className="mt-1 text-xs text-muted-foreground">{source.note}</p>}
  </>;
}

/** Keeps the existing link/button and name styling; batches summaries by symbol. */
export function StockName({ symbol, name, children, inline = false, className, expandableDescription = false }: {
  symbol?: string | null; name?: ReactNode; children?: ReactNode; inline?: boolean; className?: string; expandableDescription?: boolean;
}) {
  return <span className={cn('stock-identity min-w-0', className)} data-stock-identity={descriptionSymbol(symbol) || undefined}>
    <span data-stock-name>{children ?? name ?? symbol}</span><StockDescription symbol={symbol} inline={inline} expandable={expandableDescription} />
  </span>;
}

export function StockOptionName({symbol:raw,name}:{symbol?:string|null;name?:string|null}) {
  const symbol=descriptionSymbol(raw);
  const subscribe=useCallback((fn:()=>void)=>subscribeDescription(symbol,fn),[symbol]);
  const snapshot=useSyncExternalStore(subscribe,()=>getDescriptionSnapshot(symbol),()=>getDescriptionSnapshot(symbol));
  const description=snapshot.state==='ready' ? snapshot.record?.description||'简介待补充' : snapshot.state==='error'?'简介读取失败':'简介读取中…';
  return <>{name||raw}{symbol&&`（${description}${snapshot.record?.source.review_required?' · 待核验':''}）`}</>;
}

export function StockList({symbols,nameOf,empty='—'}:{symbols:string[];nameOf?:(symbol:string)=>ReactNode;empty?:string}) {
  return <>{symbols.length ? symbols.map((symbol,i)=><span key={`${symbol}:${i}`}>{i>0?'、':''}<StockName symbol={symbol} name={nameOf?.(symbol)??symbol} inline /></span>) : empty}</>;
}
